import { useEffect, useState } from 'preact/hooks';
import { BaseDialog } from '@components/dialog';
import { useBodyScrollLock } from 'client/lib/hooks/use-body-scroll-lock';
import { MiraseeLogo } from '@course/components/mirasee-logo';
import { rpx } from 'client/lib/rpx-client';
import {
  INTAKE_OTHER_MAX,
  INTAKE_QUESTIONS,
  IntakeQ1,
  IntakeQ2,
  IntakeQ3,
  IntakeResults as Results,
  intakeGoalPhrase,
} from 'shared/aip-intake';
import { IntakeResults } from './intake-results';

const COPY = {
  welcomeTitle: 'Welcome to AI Playground',
  welcomeBody: [
    "There's a rich library of trainings here in the AI Playground, plus a new session every week. You don't need to wade through everything on your own, though!",
    "Tell us a bit about you and your business, and we'll point you to the recordings that will help you most right now. That way you can start putting AI to work this week. :-)",
  ],
  welcomeMeta: '3 questions · under a minute',
  start: 'Find my starting point',
  skip: 'Skip for now',
  otherLabel: 'In your own words',
  otherPlaceholder: 'How you work with AI today',
  next: 'Next',
  back: 'Back',
  submit: 'See my recordings',
  picking: 'Picking your recordings…',
  error: "We couldn't pick your recordings just now. Try again.",
  retry: 'Try again',
};

export type IntakeStep = 'welcome' | 'q1' | 'q2' | 'q3' | 'picking' | 'error' | 'results';

type Answers = { q1?: IntakeQ1; q1Other: string; q2: IntakeQ2[]; q3?: IntakeQ3 };

const EMPTY: Answers = { q1Other: '', q2: [] };

export function IntakeModal({
  initialStep = 'welcome',
  initialResults,
  preview,
  fromLink,
  onClose,
}: {
  initialStep?: IntakeStep;
  initialResults?: Results;
  preview?: boolean;
  fromLink?: boolean;
  onClose(outcome: { results?: Results }): void;
}) {
  const [step, setStep] = useState<IntakeStep>(initialStep);
  const [answers, setAnswers] = useState<Answers>(EMPTY);
  const [results, setResults] = useState<Results | undefined>(initialResults);

  useBodyScrollLock(true);

  useEffect(() => {
    if (initialStep === 'welcome' && !preview) {
      rpx.aipIntake.markShown().catch(() => {});
    }
  }, []);

  function close() {
    if (step === 'picking') {
      return;
    }
    const isSkip = step === 'welcome' || step === 'q1' || step === 'q2' || step === 'q3';
    if (isSkip && !results && !preview && !fromLink) {
      rpx.aipIntake.skip().catch(() => {});
    }
    onClose({ results });
  }

  async function submit() {
    if (!answers.q1 || !answers.q3 || !answers.q2.length) {
      return;
    }
    setStep('picking');
    try {
      const r = await rpx.aipIntake.submit({
        q1: answers.q1,
        q1Other: answers.q1 === 'other' ? answers.q1Other.trim() : undefined,
        q2: answers.q2,
        q3: answers.q3,
        preview: preview || undefined,
      });
      setResults(r);
      setStep('results');
    } catch {
      setStep('error');
    }
  }

  const wide = step === 'results' || step === 'picking';

  return (
    <BaseDialog onClose={close} contentWidth>
      <div class={`overflow-y-auto w-full ${wide ? 'sm:w-[48rem]' : 'sm:w-[40rem]'}`}>
        <div class="relative p-6 sm:p-8">
          {step !== 'picking' && (
            <button
              type="button"
              aria-label="Close"
              class="absolute top-3 right-3 inline-flex items-center justify-center w-10 h-10 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              onClick={close}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="w-5 h-5"
                aria-hidden="true"
              >
                <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
          {step === 'welcome' && <WelcomeStep onStart={() => setStep('q1')} onSkip={close} />}
          {(step === 'q1' || step === 'q2' || step === 'q3') && (
            <QuestionStep
              step={step}
              answers={answers}
              setAnswers={setAnswers}
              onBack={
                fromLink && step === 'q1'
                  ? undefined
                  : () => setStep(step === 'q1' ? 'welcome' : step === 'q2' ? 'q1' : 'q2')
              }
              onNext={() => (step === 'q3' ? submit() : setStep(step === 'q1' ? 'q2' : 'q3'))}
            />
          )}
          {step === 'picking' && (
            <div class="flex flex-col items-center gap-4 py-16" role="status">
              <span class="w-8 h-8 rounded-full border-4 border-[#003DA6]/20 border-t-[#003DA6] animate-spin" />
              <p class="text-gray-600">{COPY.picking}</p>
            </div>
          )}
          {step === 'error' && (
            <div class="flex flex-col items-center gap-4 py-12 text-center" role="alert">
              <p class="text-gray-700">{COPY.error}</p>
              <button
                type="button"
                class="min-h-11 px-6 rounded-full bg-[#003DA6] text-white font-semibold hover:bg-[#002d7a]"
                onClick={submit}
              >
                {COPY.retry}
              </button>
            </div>
          )}
          {step === 'results' && results && (
            <IntakeResults
              results={results}
              subtitle={
                answers.q3
                  ? `Picked from our call recordings to help with ${intakeGoalPhrase(answers.q3)}.`
                  : undefined
              }
              onRestart={() => {
                setAnswers(EMPTY);
                setStep('q1');
              }}
              onFinish={() => onClose({ results })}
            />
          )}
        </div>
      </div>
    </BaseDialog>
  );
}

function WelcomeStep({ onStart, onSkip }: { onStart(): void; onSkip(): void }) {
  return (
    <div class="flex flex-col items-center text-center gap-5 py-2">
      <MiraseeLogo class="scale-125" />
      <h2 class="text-2xl font-semibold text-gray-900">{COPY.welcomeTitle}</h2>
      <div class="flex flex-col gap-3 text-gray-600 max-w-md">
        {COPY.welcomeBody.map((p) => (
          <p key={p}>{p}</p>
        ))}
      </div>
      <p class="text-sm font-medium text-gray-500">{COPY.welcomeMeta}</p>
      <div class="flex flex-col items-center gap-1">
        <button
          type="button"
          class="min-h-12 px-8 rounded-full bg-[#003DA6] text-white font-semibold hover:bg-[#002d7a]"
          onClick={onStart}
        >
          {COPY.start}
        </button>
        <button
          type="button"
          class="min-h-11 px-3 text-sm font-medium text-gray-500 hover:text-gray-800"
          onClick={onSkip}
        >
          {COPY.skip}
        </button>
      </div>
    </div>
  );
}

function QuestionStep({
  step,
  answers,
  setAnswers,
  onBack,
  onNext,
}: {
  step: 'q1' | 'q2' | 'q3';
  answers: Answers;
  setAnswers(a: Answers): void;
  onBack?(): void;
  onNext(): void;
}) {
  const index = step === 'q1' ? 0 : step === 'q2' ? 1 : 2;
  const question = INTAKE_QUESTIONS[index];

  function isSelected(id: string) {
    if (step === 'q1') {
      return answers.q1 === id;
    }
    if (step === 'q2') {
      return answers.q2.includes(id as IntakeQ2);
    }
    return answers.q3 === id;
  }

  function pick(id: string) {
    if (step === 'q1') {
      setAnswers({ ...answers, q1: id as IntakeQ1 });
      return;
    }
    if (step === 'q3') {
      setAnswers({ ...answers, q3: id as IntakeQ3 });
      return;
    }
    const q2 = id as IntakeQ2;
    if (answers.q2.includes(q2)) {
      setAnswers({ ...answers, q2: answers.q2.filter((x) => x !== q2) });
      return;
    }
    setAnswers({
      ...answers,
      q2: q2 === 'none' ? ['none'] : [...answers.q2.filter((x) => x !== 'none'), q2],
    });
  }

  const answered =
    step === 'q1'
      ? !!answers.q1 && (answers.q1 !== 'other' || !!answers.q1Other.trim())
      : step === 'q2'
        ? answers.q2.length > 0
        : !!answers.q3;

  return (
    <div class="flex flex-col gap-5">
      <div class="flex items-center justify-between pr-10">
        <p class="text-sm font-semibold text-gray-500">Question {index + 1} of 3</p>
        <div class="flex gap-1.5" aria-hidden="true">
          {[0, 1, 2].map((n) => (
            <span
              key={n}
              class={`h-2 rounded-full ${n === index ? 'w-5' : 'w-2'} ${n <= index ? 'bg-[#003DA6]' : 'bg-gray-300'}`}
            />
          ))}
        </div>
      </div>
      <fieldset class="flex flex-col gap-5">
        <legend class="flex flex-col gap-1.5 mb-5">
          <span class="block text-xl font-semibold text-gray-900">{question.text}</span>
          <span class="block text-gray-500">{question.hint}</span>
        </legend>
        <div class="flex flex-col gap-2">
          {question.options.map((o) => {
            const on = isSelected(o.id);
            return (
              <button
                key={o.id}
                type="button"
                role={question.multi ? 'checkbox' : 'radio'}
                aria-checked={on}
                class={`flex items-center gap-3.5 w-full min-h-13 px-4 py-3 rounded-xl border-[1.5px] text-left text-gray-900 ${
                  on
                    ? 'border-[#003DA6] bg-[#f0f5fd]'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                onClick={() => pick(o.id)}
              >
                <span
                  aria-hidden="true"
                  class={`shrink-0 w-[18px] h-[18px] ${question.multi ? 'rounded-[5px]' : 'rounded-full'} ${
                    on
                      ? question.multi
                        ? 'border-[1.5px] border-[#003DA6] bg-[#003DA6]'
                        : 'border-[6px] border-[#003DA6] bg-white'
                      : 'border-[1.5px] border-gray-400 bg-white'
                  }`}
                />
                <span>{o.label}</span>
              </button>
            );
          })}
          {step === 'q1' && answers.q1 === 'other' && (
            <div class="flex flex-col gap-1.5 pl-8 pt-1">
              <label for="intake-q1-other" class="text-sm font-medium text-gray-700">
                {COPY.otherLabel}
              </label>
              <input
                id="intake-q1-other"
                type="text"
                maxLength={INTAKE_OTHER_MAX}
                value={answers.q1Other}
                placeholder={COPY.otherPlaceholder}
                class="min-h-11 px-3.5 rounded-lg border border-gray-300 text-gray-900"
                onInput={(e) => setAnswers({ ...answers, q1Other: e.currentTarget.value })}
              />
            </div>
          )}
        </div>
      </fieldset>
      <div class="flex justify-between items-center pt-1">
        {onBack ? (
          <button
            type="button"
            class="min-h-11 px-3 text-gray-600 font-medium hover:text-gray-900"
            onClick={onBack}
          >
            {COPY.back}
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          disabled={!answered}
          class="min-h-11 px-7 rounded-full font-semibold bg-[#003DA6] text-white hover:bg-[#002d7a] disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
          onClick={onNext}
        >
          {step === 'q3' ? COPY.submit : COPY.next}
        </button>
      </div>
    </div>
  );
}
